import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES, ICONS_PALETTE } from '_constants/icons';
import { ROUTES } from '_constants/routes';
import { Virtual_Address_FAQ } from '_constants/faq';

import { Divider } from '_components/atomic/layouts';
import Circle from '_components/atomic/Circle';
import Dots from '_components/atomic/Dots';
import FancyList from '_components/atomic/FancyList';
import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import HowSection from '_components/composed/sections/HowSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import ProcessSection from '_components/composed/sections/ProcessSection';
import FAQSection from '_components/composed/sections/FAQSection';
import ProductBannerSection from '_components/composed/sections/ProductBannerSection';
import BottomNavigationSection from '_components/composed/sections/BottomNavigationSection';

const { colors, spaces, fontSizes, devices } = theme;

const StyledVirtualAddressPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const StyleButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${spaces.regular}px;
  gap: ${spaces.regular}px;
  background: ${colors.indigo[500]};
  border-radius: ${spaces.regular}px;

  .price {
    font-family: 'Aeonik';
    font-weight: 500;
    font-size: ${fontSizes.large2X}px;
    line-height: 150%;
    color: ${colors.indigo[25]};
  }
  @media ${devices.tablet} {
    flex-direction: column;
    .price {
      font-size: ${fontSizes.regular}px;
    }
  }
`;

const VirtualAddressPage = () => {
  return (
    <StyledVirtualAddressPage>
      <ProductBannerSection
        title="Virtual Address"
        text="A virtual address offers you a cost-effective solution to enhance your business's professional image, without the cost and hassle of a physical office or store front.
              Whether you're a freelancer, startup, or established business looking to expand, a virtual address gives you the prestige of a prime business location, while allowing you to receive mail and packages remotely. This way, you can focus on growing your business, without sacrificing privacy and security. Sign up for a virtual address today and take your work-from-home or remote business to the next level."
        buttonsBlock={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={colors.indigo[600]}
              />
            }
            color={colors.white}
            textColor={colors.indigo[600]}
            hoverColor={colors.white}
            hoverTextColor={colors.indigo[600]}
          >
            Get Virtual Address
          </Button>
        }
        backgroundImage={`${config.basePath}/images/virtual-address-banner-background.svg`}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <HowSection
        title={<>Benefits of Using a Virtual Address</>}
        rightPart={
          <FancyList
            items={[
              {
                title: 'Professionalism and Credibility',
                description:
                  'A virtual address gives your business the appearance of being located in a prime business location, enhancing your professional image and credibility.',
                icon: ICONS_ALIASES.work,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.work],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
              {
                title: 'Privacy and Security',
                description:
                  'With a virtual address, you can protect your personal or business information, as mail and packages are received and managed by a trusted third-party service provider.',
                icon: ICONS_ALIASES.verified,
                iconPalette: ICONS_PALETTE.green[ICONS_ALIASES.verified],
                color: colors.green[50],
                textColor: colors.green[500],
              },
              {
                title: 'Remote Mail and Package Receipt',
                description:
                  'By using a virtual address, you can receive mail and packages remotely, freeing up your time and energy, and reducing the risk of lost or stolen mail.',
                icon: ICONS_ALIASES.vote,
                iconPalette: ICONS_PALETTE.orange[ICONS_ALIASES.vote],
                color: colors.orange[50],
                textColor: colors.orange[500],
              },
              {
                title: 'Cost-Effectiveness',
                description:
                  'A virtual address provides a cost-effective solution to the challenges of running a remote or work-from-home business, as it eliminates the need for a physical office or store front.',
                icon: ICONS_ALIASES.moneyBag,
                iconPalette: ICONS_PALETTE.blue[ICONS_ALIASES.moneyBag],
                color: colors.blue[25],
                textColor: colors.blue[500],
              },
            ]}
          />
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <ProcessSection
        title="How Does it Work?"
        activeStepIdx={0}
        steps={[
          {
            title: 'STEP 01',
            subtitle: 'Sign Up',
            description: (
              <span>
                Provide us with the necessary information, such as your name and
                contact details, to set up your account.
              </span>
            ),
            imageSrc: `${config.basePath}/images/telecommuting.svg`,
            imageAlt: 'Cartoon working at laptop',
            reverse: false,
          },
          {
            title: 'STEP 02',
            subtitle: 'Choose Your Location',
            description: (
              <span>
                Choose the State and City where you want your business address.
              </span>
            ),
            imageSrc: `${config.basePath}/images/globalization.svg`,
            reverse: true,
          },
          {
            title: 'STEP 03',
            subtitle: 'Receive Mail and Packages',
            description: (
              <span>
                Once your virtual address is set up, start receiving mail and
                packages at your virtual address.
              </span>
            ),
            imageSrc: `${config.basePath}/images/mailbox.svg`,
            reverse: false,
          },
          {
            title: 'STEP 04',
            subtitle: 'Manage Your Mail and Packages',
            description: (
              <span>
                Easily manage your mail and packages from your new business
                address through our online portal. View, manage, and retrieve
                your mail and packages from anywhere, at any time.
              </span>
            ),
            imageSrc: `${config.basePath}/images/mail-sent.svg`,
            reverse: true,
          },
        ]}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomBannerSection
        header={<>Maintain Your Privacy.</>}
        footer={
          <div className="buttonsContainer">
            <StyleButtonContainer>
              <Button
                color={colors.white}
                textColor={colors.indigo[600]}
                hoverColor={colors.white}
                hoverTextColor={colors.indigo[600]}
              >
                Open Your Virtual Business Address Today
              </Button>
              <div className="price">Just $24.99</div>
            </StyleButtonContainer>
          </div>
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <FAQSection items={Virtual_Address_FAQ} />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomNavigationSection
        previousService={{
          title: 'EIN/Tax ID',
          link: ROUTES.einTaxId,
        }}
        nextService={{
          title: 'LLC Incorporation',
          link: ROUTES.llcIncorporation,
        }}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(18% + 0px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={-90}
        left={49}
        top={`calc(14% - 20px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={260}
        top={`calc(22% - 30px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-172}
        top={`calc(18% - 20px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(83% + 0px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={90}
        left={-134}
        top={`calc(77% - 20px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={296}
        top={`calc(71% - 10px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-91}
        top={`calc(68% + 30px)`}
      />
    </StyledVirtualAddressPage>
  );
};

export default VirtualAddressPage;
